import { FC } from 'react';

import { BannerImageCropType, ImageComponent } from 'utils/constants/banner';

import generateImageCropReplaceButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_design/generate_image_crop_replace_button_click';
import campaignCreateCropReplaceButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_create_crop_replace_button_click';
import { Card, Loader, VSpacing } from '@hh.ru/magritte-ui';

import { Description } from 'components/common/BannerImg/CropImageModal/ImageContent/Description/Description';
import { ImageActions } from 'components/common/BannerImg/CropImageModal/ImageContent/ImageActions/ImageActions';
import { ImageCrop } from 'components/common/BannerImg/CropImageModal/ImageContent/ImageCrop/ImageCrop';
import { ImageInput } from 'components/common/BannerImg/CropImageModal/ImageContent/ImageInput/ImageInput';
import { UpscaleLoader } from 'components/common/BannerImg/CropImageModal/ImageContent/UpscaleLoader/UpscaleLoader';

import {
    ImageInfo,
    setBannerImageComponentByType,
    setBannerImageUpscaleStatusByType,
    setIsUpscaleTipVisible,
} from 'store/models/createBanner';
import { setIsGalleryModalVisible } from 'store/models/gallery';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerImg/CropImageModal/ImageContent/ImageContent.module.less';

interface ImageContentProps {
    cropType: BannerImageCropType;
    isSomeCropLoading: boolean;
    isQuickCampaign?: boolean;
    imageData: Partial<ImageInfo>;
}

export const ImageContent: FC<ImageContentProps> = ({ isSomeCropLoading, isQuickCampaign, imageData, cropType }) => {
    const { width, height, url, cropInfo, component, isLoadedInCrop, upscaleStatus } = imageData;

    const dispatch = useDispatch();

    const isCropImageModalWasOpenedFromAiImageGenerationModal = useSelector(
        (state) => state.createBanner.isCropImageModalWasOpenedFromAiImageGenerationModal
    );

    const loader = upscaleStatus ? (
        <UpscaleLoader cropType={cropType} />
    ) : (
        <Card stretched borderWidth="default">
            <div className={styles.imageContentLoading}>
                <Loader size={32} />
            </div>
        </Card>
    );

    const uploadNewImage = () => {
        if (isCropImageModalWasOpenedFromAiImageGenerationModal) {
            generateImageCropReplaceButtonClick({
                bannerImageCropType: cropType,
            });
        }
        campaignCreateCropReplaceButtonClick({ bannerImageCropType: cropType });

        dispatch(setIsUpscaleTipVisible(false));
        dispatch([
            setBannerImageComponentByType({
                cropType,
                component: ImageComponent.Loading,
            }),
            setBannerImageUpscaleStatusByType({
                cropType,
                upscaleStatus,
            }),
            setIsUpscaleTipVisible(false),
            setIsGalleryModalVisible(true),
        ]);
    };

    return (
        <div className={styles.imageContentContainer} data-qa={`image-content-${cropType}`}>
            <Description cropType={cropType} disabled={!cropInfo} />
            <VSpacing default={24} />
            {component === ImageComponent.Loading && loader}
            {component === ImageComponent.Crop && cropInfo && width && height && url && (
                <div>
                    <ImageCrop
                        cropType={cropType}
                        cropInfo={cropInfo}
                        width={width}
                        height={height}
                        url={url}
                        isLoadedInCrop={isLoadedInCrop}
                        isQuickCampaign={isQuickCampaign}
                    />
                    {!isQuickCampaign && (
                        <>
                            <VSpacing default={12} />
                            <ImageActions
                                cropType={cropType}
                                isSomeCropLoading={isSomeCropLoading}
                                uploadNewImage={uploadNewImage}
                                upscaleStatus={upscaleStatus}
                            />
                        </>
                    )}
                </div>
            )}
            {component === ImageComponent.Input && (
                <ImageInput cropType={cropType} isSomeCropLoading={isSomeCropLoading} uploadNewImage={uploadNewImage} />
            )}
        </div>
    );
};
